import { useEffect, useRef, useState } from "react";
import {
  ApiError,
  callLmeFinalize,
  deleteLmeFinalizeDraft,
  extractLmeScenarioFromContext,
  fetchLmeFinalizeDraft,
  persistLmeFinalize,
  syncLmeScenario,
  updateLmeDealScenario,
} from "../../api";
import type {
  ChatTurn,
  ExtractedConfidence,
  FinalizeDraftSource,
  FinalizedEmail,
  FinalizeResponse,
  Scenario,
  SyncScenarioResponse,
} from "../../types/lme";

// Exported so LME-3's scenario-edit dialog reuses the exact same Zoho-valid
// picklist values (no drift between the preflight form and inline editing).
export const LOAN_PRODUCTS = [
  "BPL Bridge",
  "DSCR",
  "Hard Money",
  "Fix & Flip",
  "Construction",
  "Other",
] as const;
export const LIEN_POSITIONS = ["1st", "2nd", "3rd"] as const;
export const PROP_TYPES = ["SFR", "2-4 Unit", "Condo", "Multifamily", "Mixed Use", "Commercial"] as const;
export const OCCUPANCIES = ["Owner Occupied", "Non Owner Occupied"] as const;
export const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA","KS","KY","LA","ME","MD",
  "MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ","NM","NY","NC","ND","OH","OK","OR","PA","RI","SC",
  "SD","TN","TX","UT","VT","VA","WA","WV","WI","WY","DC",
] as const;

/**
 * Finalize Q&A wizard.
 *
 * Phase B refactor adds:
 *   - Three-button footer (Close / Apply Revision / Finalize Email)
 *   - Draft restore on mount via GET /finalize-draft/:dealId — picks up
 *     mid-Q&A or mid-revision state from KV (7d TTL). Worker auto-saves
 *     the draft on every /finalize round-trip; no SPA save needed.
 *   - Finalize Email: lazy idempotency key, calls /persist-finalize,
 *     on success the worker clears the KV draft and creates the v{N} Note.
 *
 * State machine:
 *   loading      → initial mount, fetching draft + maybe first /finalize call
 *   questions    → render numbered Q&A inputs
 *   generated    → render email preview + revise textbox + Finalize Email
 *   finalizing   → /persist-finalize in flight; both action buttons disabled
 *   error        → inline error + retry
 *
 * Close stays enabled in every phase except "finalizing" — partial work
 * survives in KV for 7 days; reopening picks up exactly where the user left
 * off (verified by close-mid-Q&A-reopen browser test).
 */
interface LmeFinalizeModalProps {
  dealId: string;
  selectedLenderIds: string[];
  /** Phase B.2 FIX 5: pre-populates the preflight form when /finalize returns
   *  400 missing-fields. Sourced from LmeTab's match.deal_snapshot.scenario. */
  initialScenario: Scenario | null;
  /** Addition 1 — pre-fill the revise box (+ select "Change all") once the
   *  email is restored, so the staleness "Update email to match" button lands
   *  the user one click from APPLY REVISION. They still review + FINALIZE. */
  initialRevision?: string;
  onClose: () => void;
  onToast: (msg: string) => void;
}

type Phase = "loading" | "preflight" | "questions" | "generated" | "finalizing" | "error";

/** Phase B.2 FIX 5 — local state for the 6-field preflight form. Mirrors the
 *  Scenario keys the worker /update-deal-scenario route writes back. */
interface PreflightFields {
  loanAmount: string;
  loanProduct: string;
  lienPosition: string;
  propType: string;
  propState: string;
  occupancy: string;
}

// LME-2a — the revise dropdown: "Change all" + the 8 finalized-email sections.
// Keys match the worker's REVISION_SECTION_KEYS / parser section names.
const REVISION_TARGETS: Array<{ key: string; label: string }> = [
  { key: "ALL", label: "Change all (whole email)" },
  { key: "SUBJECT", label: "Subject" },
  { key: "INTRO", label: "Intro" },
  { key: "KPI_BOXES", label: "KPI boxes" },
  { key: "PROPERTY_LOAN_DETAILS", label: "Property & loan details" },
  { key: "BORROWER_INFO", label: "Borrower information" },
  { key: "USE_AND_EXIT", label: "Use of funds & exit" },
  { key: "NARRATIVE", label: "Narrative" },
  { key: "FLAGS", label: "Flags" },
];

function scenarioToPreflight(s: Scenario | null): PreflightFields {
  return {
    loanAmount: s?.loanAmount != null ? String(s.loanAmount) : "",
    loanProduct: s?.loanProduct ?? "",
    lienPosition: s?.lienPosition ?? "",
    propType: s?.propType ?? "",
    propState: s?.propState ?? "",
    occupancy: s?.occupancy ?? "",
  };
}

export function LmeFinalizeModal({
  dealId,
  selectedLenderIds,
  initialScenario,
  initialRevision,
  onClose,
  onToast,
}: LmeFinalizeModalProps) {
  const [chatHistory, setChatHistory] = useState<ChatTurn[]>([]);
  const [phase, setPhase] = useState<Phase>("loading");
  const [questions, setQuestions] = useState<string[]>([]);
  const [answers, setAnswers] = useState<string[]>([]);
  const [generated, setGenerated] = useState<FinalizedEmail | null>(null);
  const [revisionRequest, setRevisionRequest] = useState("");
  // LME-2a — which section the revision targets ("ALL" or a section key).
  const [revisionTarget, setRevisionTarget] = useState<string>("ALL");
  // Addition 1 — fire the initial-revision pre-fill exactly once.
  const prefilledRef = useRef(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  // Lazy — one key per modal mount session, reused on Finalize Email retry
  // so /persist-finalize dedupes if first call landed but response was lost.
  const [persistIdempotencyKey] = useState(() => crypto.randomUUID());

  // Phase B.2 FIX 4: when the modal restored content from Zoho's
  // Finalized_Email_JSON (vs. a fresh wizard or KV draft), surface
  // "Start from scratch" link in footer.
  const [draftSource, setDraftSource] = useState<FinalizeDraftSource | null>(null);

  // Addition 1: once the restored email is showing, drop the auto-built revision
  // into the box and select "Change all" — the user clicks APPLY REVISION,
  // reviews, then FINALIZE. Fires once.
  useEffect(() => {
    if (!initialRevision || prefilledRef.current) return;
    if (phase !== "generated") return;
    prefilledRef.current = true;
    setRevisionRequest(initialRevision);
    setRevisionTarget("ALL");
  }, [initialRevision, phase]);

  // Phase B.2 FIX 5: preflight form state, used when /finalize returns 400
  // for missing scenario fields. Pre-populated from the LmeTab's scenario.
  const [preflight, setPreflight] = useState<PreflightFields>(() =>
    scenarioToPreflight(initialScenario),
  );
  const [preflightExtracted, setPreflightExtracted] = useState<Partial<Record<keyof PreflightFields, true>>>({});
  const [preflightConfidence, setPreflightConfidence] = useState<ExtractedConfidence>({});
  const [extracting, setExtracting] = useState(false);
  const [savingScenario, setSavingScenario] = useState(false);
  const [missingFields, setMissingFields] = useState<string[]>([]);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      // Close stays enabled unless we're mid-persist or mid-scenario-write
      // (both write to Zoho and would leave partial state on cancel).
      if (e.key === "Escape" && phase !== "finalizing" && !savingScenario && !extracting) {
        onClose();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose, phase, savingScenario, extracting]);

  const callFinalize = async (
    nextHistory: ChatTurn[],
    revision: string | null,
    // LME-2a: on a revision, the targeted section + the email to edit (base).
    target?: string | null,
    baseEmail?: FinalizedEmail,
  ) => {
    setPhase("loading");
    setSubmitting(true);
    setError(null);
    try {
      const r = await callLmeFinalize({
        deal_id: dealId,
        selected_lender_ids: selectedLenderIds,
        chat_history: nextHistory,
        revision_request: revision,
        revision_target: revision ? target ?? "ALL" : undefined,
        base_email: revision ? baseEmail : undefined,
      });
      setChatHistory(r.chat_history);
      if (r.phase === "questions") {
        setQuestions(r.questions);
        setAnswers(new Array(r.questions.length).fill(""));
        setGenerated(null);
        setPhase("questions");
      } else {
        setGenerated(wireToFinalized(r));
        setPhase("generated");
        setRevisionRequest("");
      }
    } catch (err) {
      // Phase B.2 FIX 5: if /finalize 400'd with a missing-fields list, land
      // in the preflight form instead of an opaque error pill.
      if (err instanceof ApiError) {
        const body = err.body as
          | { error?: { code?: string; details?: { missing?: string[] } } }
          | undefined;
        const missing = body?.error?.details?.missing;
        if (err.status === 400 && Array.isArray(missing) && missing.length > 0) {
          setMissingFields(missing);
          setPhase("preflight");
          return;
        }
      }
      setError(formatErr(err));
      setPhase("error");
    } finally {
      setSubmitting(false);
    }
  };

  const onExtractScenario = async () => {
    setExtracting(true);
    setError(null);
    try {
      const r = await extractLmeScenarioFromContext(dealId);
      const next: PreflightFields = { ...preflight };
      const extractedKeys: Partial<Record<keyof PreflightFields, true>> = {};
      if (r.extracted.loanAmount != null) {
        next.loanAmount = String(r.extracted.loanAmount);
        extractedKeys.loanAmount = true;
      }
      if (r.extracted.loanProduct) {
        next.loanProduct = r.extracted.loanProduct;
        extractedKeys.loanProduct = true;
      }
      if (r.extracted.lienPosition) {
        next.lienPosition = r.extracted.lienPosition;
        extractedKeys.lienPosition = true;
      }
      if (r.extracted.propType) {
        next.propType = r.extracted.propType;
        extractedKeys.propType = true;
      }
      if (r.extracted.propState) {
        next.propState = r.extracted.propState;
        extractedKeys.propState = true;
      }
      if (r.extracted.occupancy) {
        next.occupancy = r.extracted.occupancy;
        extractedKeys.occupancy = true;
      }
      setPreflight(next);
      setPreflightExtracted(extractedKeys);
      setPreflightConfidence(r.confidence);
      const filled = Object.keys(extractedKeys).length;
      if (filled === 0) {
        onToast("Claude didn't find any scenario fields in this deal's emails or notes.");
      } else {
        onToast(`✓ Claude filled ${filled} field${filled === 1 ? "" : "s"} — review before saving`);
      }
    } catch (err) {
      setError(formatErr(err));
    } finally {
      setExtracting(false);
    }
  };

  const onSaveAndContinue = async () => {
    // Validate all 6 fields are populated. Number-coerce loanAmount.
    const amt = parseFloat(preflight.loanAmount.replace(/[,$\s]/g, ""));
    if (!isFinite(amt) || amt <= 0) {
      setError("Loan amount is required and must be a positive number.");
      return;
    }
    if (!preflight.loanProduct || !preflight.lienPosition || !preflight.propType || !preflight.propState || !preflight.occupancy) {
      setError("All 6 fields are required before continuing.");
      return;
    }
    setSavingScenario(true);
    setError(null);
    try {
      await updateLmeDealScenario({
        deal_id: dealId,
        fields: {
          loanAmount: amt,
          loanProduct: preflight.loanProduct,
          lienPosition: preflight.lienPosition,
          propType: preflight.propType,
          propState: preflight.propState,
          occupancy: preflight.occupancy,
        },
      });
      onToast("✓ Scenario saved to Zoho — continuing to wizard");
      setMissingFields([]);
      setPreflightExtracted({});
      setPreflightConfidence({});
      // Kick off the wizard now that Zoho has the fields the worker needs.
      await callFinalize([], null);
    } catch (err) {
      setError(formatErr(err));
    } finally {
      setSavingScenario(false);
    }
  };

  const onStartFromScratch = async () => {
    setError(null);
    setDraftSource(null);
    try {
      await deleteLmeFinalizeDraft(dealId);
    } catch {
      // Best-effort — continue even if KV delete fails.
    }
    setGenerated(null);
    setChatHistory([]);
    setRevisionRequest("");
    // Call /finalize directly, bypassing the /finalize-draft restore path.
    await callFinalize([], null);
  };

  // Initial mount: try draft restore first. If draft has a latest_email,
  // restore directly to generated phase. If draft has chat_history but no
  // email, restore to questions phase by replaying the last Claude turn's
  // questions (we already have them in chat_history). If no draft, run
  // initial /finalize.
  //
  // Phase B.2 FIX 4: the worker /finalize-draft now also returns a synthetic
  // draft (source="zoho_finalized") when the deal has a Finalized_Email_JSON
  // persisted. SPA treats it identically to a KV draft for restore purposes
  // but tracks the source so the footer "Start from scratch" link appears.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetchLmeFinalizeDraft(dealId);
        if (cancelled) return;
        const d = r.draft;
        if (d && d.latest_email) {
          setChatHistory(d.chat_history);
          setGenerated(d.latest_email);
          setAnswers(d.answers);
          setPhase("generated");
          setDraftSource(d.source ?? "kv_draft");
          if (d.source === "zoho_finalized") {
            onToast("Loaded finalized email — edit and click FINALIZE EMAIL to save a new version");
          } else {
            onToast("Draft restored — picked up where you left off");
          }
          return;
        }
        if (d && d.chat_history.length > 0) {
          const lastAssistant = [...d.chat_history]
            .reverse()
            .find((t) => t.role === "assistant");
          if (lastAssistant) {
            const qs = extractQuestionsClient(lastAssistant.text);
            if (qs.length > 0) {
              setChatHistory(d.chat_history);
              setQuestions(qs);
              setAnswers(d.answers.length === qs.length ? d.answers : new Array(qs.length).fill(""));
              setPhase("questions");
              setDraftSource(d.source ?? "kv_draft");
              onToast("Draft restored — picked up where you left off");
              return;
            }
          }
        }
        // No usable draft — run initial /finalize. If it 400s with missing
        // fields, callFinalize will land us in "preflight" instead of "error".
        await callFinalize([], null);
      } catch {
        if (cancelled) return;
        // Draft fetch failure is non-fatal; just run initial finalize.
        await callFinalize([], null);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dealId]);

  const submitAnswers = () => {
    const text = answers
      .map((a, i) => ({ idx: i, ans: a.trim() }))
      .filter((x) => x.ans.length > 0)
      .map((x) => `${x.idx + 1}. ${x.ans}`)
      .join("\n");
    if (!text) {
      onToast("Type answers first.");
      return;
    }
    const userTurn: ChatTurn = { role: "user", text: `ANSWERS:\n${text}` };
    const nextHistory = [...chatHistory, userTurn];
    // Phase B.3: write the answers' field values back to Zoho in the background
    // (decision: sync on Q&A submit, not deferred to FINALIZE EMAIL). Runs in
    // parallel with email generation; never blocks the wizard. A specific toast
    // tells the user exactly which fields landed in the CRM.
    void syncLmeScenario(dealId, nextHistory)
      .then((r) => onToast(buildSyncToast(r)))
      .catch(() =>
        onToast("⚠ Couldn't sync answers to Zoho (email still generating)"),
      );
    void callFinalize(nextHistory, null);
  };

  const submitRevision = () => {
    const r = revisionRequest.trim();
    if (!r) {
      onToast("Type a revision request first.");
      return;
    }
    // Phase B.3: revisions often carry factual corrections ("actually FICO is
    // 740", "appraisal came back, ARV is $1.2M"). Sync them too — same
    // parallel, non-blocking pattern. The revision text is appended as a user
    // turn so the extractor sees it (mirrors the worker's [REVISION_REQUEST]
    // message). Unlike Q&A submit, only toast when something actually synced —
    // a stylistic-only revision extracts 0 fields and must not spam a toast.
    const syncHistory: ChatTurn[] = [
      ...chatHistory,
      { role: "user", text: `[REVISION_REQUEST]\n${r}` },
    ];
    void syncLmeScenario(dealId, syncHistory)
      .then((res) => {
        if (res.fields_written.length > 0 || res.notes_appended) {
          onToast(buildSyncToast(res));
        }
      })
      .catch(() =>
        onToast("⚠ Couldn't sync revision to Zoho (email still updating)"),
      );
    void callFinalize(chatHistory, r, revisionTarget, generated ?? undefined);
  };

  // LME-6: inline manual edits to the generated email. Each updates `generated`
  // in place; saved when the user clicks FINALIZE EMAIL. No AI, no Zoho write,
  // no re-score. A later from-scratch regeneration overwrites these; a targeted
  // revision of another section preserves them (the merge keeps it byte-identical).
  const patchGenerated = (patch: Partial<FinalizedEmail>) =>
    setGenerated((g) => (g ? { ...g, ...patch } : g));
  const patchKpi = (i: number, sub: "value" | "label" | "sub", v: string) =>
    setGenerated((g) =>
      g
        ? { ...g, kpiBoxes: g.kpiBoxes.map((k, idx) => (idx === i ? { ...k, [sub]: v } : k)) }
        : g,
    );
  const patchRow = (
    section: "propertyRows" | "borrowerRows" | "useRows",
    i: number,
    sub: "key" | "value",
    v: string,
  ) =>
    setGenerated((g) =>
      g
        ? { ...g, [section]: g[section].map((r, idx) => (idx === i ? { ...r, [sub]: v } : r)) }
        : g,
    );
  const patchFlag = (i: number, v: string) =>
    setGenerated((g) => (g ? { ...g, flags: g.flags.map((f, idx) => (idx === i ? v : f)) } : g));
  const addFlag = () => setGenerated((g) => (g ? { ...g, flags: [...g.flags, ""] } : g));
  const removeFlag = (i: number) =>
    setGenerated((g) => (g ? { ...g, flags: g.flags.filter((_, idx) => idx !== i) } : g));

  const finalizeEmail = async () => {
    if (!generated) return;
    setPhase("finalizing");
    setError(null);
    try {
      const result = await persistLmeFinalize(
        dealId,
        {
          subject: generated.subject,
          intro: generated.intro,
          kpi_boxes: generated.kpiBoxes,
          property_rows: generated.propertyRows,
          borrower_rows: generated.borrowerRows,
          use_rows: generated.useRows,
          narrative: generated.narrative,
          flags: generated.flags,
          finalized_at: generated.finalizedAt ?? new Date().toISOString(),
        },
        persistIdempotencyKey,
      );
      onToast(`✓ Finalized v${result.version} saved to Zoho`);
      onClose();
    } catch (err) {
      setError(formatErr(err));
      setPhase("generated"); // back to editable
    }
  };

  return (
    <div className="write-confirm-backdrop" role="dialog" aria-modal="true">
      <div className="write-confirm-card lme-finalize-card">
        <div className="write-confirm-title">FINALIZE LENDER EMAIL</div>

        {phase === "loading" && (
          <div className="lme-loading">
            <span className="chat-thinking-dot" />
            <span className="chat-thinking-dot" />
            <span className="chat-thinking-dot" />
            <span className="chat-thinking-label">ASKING CLAUDE</span>
          </div>
        )}

        {phase === "preflight" && (
          <PreflightForm
            preflight={preflight}
            setPreflight={setPreflight}
            missingFields={missingFields}
            extracted={preflightExtracted}
            confidence={preflightConfidence}
            extracting={extracting}
            savingScenario={savingScenario}
            error={error}
            onExtract={() => void onExtractScenario()}
          />
        )}

        {phase === "questions" && (
          <>
            <div className="lme-finalize-intro">
              Claude needs more info before generating the email. Answer the
              questions below — anything you skip, Claude will guess at.
            </div>
            <div className="lme-finalize-questions">
              {questions.map((q, i) => (
                <label className="lme-finalize-q" key={i}>
                  <span className="lme-finalize-q-num">{i + 1}.</span>
                  <span className="lme-finalize-q-text">{q}</span>
                  <textarea
                    className="lme-finalize-q-answer"
                    value={answers[i] ?? ""}
                    onChange={(e) => {
                      const next = [...answers];
                      next[i] = e.target.value;
                      setAnswers(next);
                    }}
                    rows={2}
                    disabled={submitting}
                  />
                </label>
              ))}
            </div>
          </>
        )}

        {(phase === "generated" || phase === "finalizing") && generated && (
          <div className="lme-finalize-generated">
            <div className="lme-finalize-edit-hint">
              ✎ Click any line to edit it — manual edits save when you Finalize.
            </div>
            <div className="lme-finalize-meta-row">
              <span className="lme-finalize-meta-label">SUBJECT</span>
              <EditableText
                value={generated.subject}
                onCommit={(v) => patchGenerated({ subject: v })}
              />
            </div>
            <div className="lme-finalize-meta-row">
              <span className="lme-finalize-meta-label">INTRO</span>
              <EditableText
                value={generated.intro}
                onCommit={(v) => patchGenerated({ intro: v })}
                multiline
              />
            </div>

            <div className="lme-finalize-section-label">KPI BOXES</div>
            <div className="lme-finalize-kpi-grid">
              {generated.kpiBoxes.map((k, i) => (
                <div className="lme-finalize-kpi" key={i}>
                  <div className="lme-finalize-kpi-val">
                    <EditableText value={k.value} onCommit={(v) => patchKpi(i, "value", v)} />
                  </div>
                  <div className="lme-finalize-kpi-lbl">
                    <EditableText value={k.label} onCommit={(v) => patchKpi(i, "label", v)} />
                  </div>
                  <div className="lme-finalize-kpi-sub">
                    <EditableText
                      value={k.sub}
                      onCommit={(v) => patchKpi(i, "sub", v)}
                      placeholder="(add sub)"
                    />
                  </div>
                </div>
              ))}
            </div>

            <DetailRows
              label="PROPERTY & LOAN"
              rows={generated.propertyRows}
              onEdit={(i, f, v) => patchRow("propertyRows", i, f, v)}
            />
            <DetailRows
              label="BORROWER"
              rows={generated.borrowerRows}
              onEdit={(i, f, v) => patchRow("borrowerRows", i, f, v)}
            />
            {generated.useRows.length > 0 && (
              <DetailRows
                label="USE & EXIT"
                rows={generated.useRows}
                onEdit={(i, f, v) => patchRow("useRows", i, f, v)}
              />
            )}

            <div className="lme-finalize-section-label">NARRATIVE</div>
            <div className="lme-finalize-narrative">
              <EditableText
                value={generated.narrative}
                onCommit={(v) => patchGenerated({ narrative: v })}
                multiline
              />
            </div>

            <div className="lme-finalize-section-label">FLAGS TO CONFIRM</div>
            <ul className="lme-finalize-flags">
              {generated.flags.map((f, i) => (
                <li key={i} className="lme-finalize-flag-item">
                  <EditableText
                    value={f}
                    onCommit={(v) => patchFlag(i, v)}
                    placeholder="(empty flag)"
                  />
                  <button
                    className="lme-finalize-flag-del"
                    onClick={() => removeFlag(i)}
                    title="Remove flag"
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
            <button className="lme-finalize-flag-add" onClick={addFlag}>
              + ADD FLAG
            </button>

            <div className="lme-finalize-section-label">REVISE (optional)</div>
            <select
              className="lme-finalize-revise-target"
              value={revisionTarget}
              onChange={(e) => setRevisionTarget(e.target.value)}
              disabled={submitting || phase === "finalizing"}
              title="Pick the section to change — every other section stays exactly as it is now"
            >
              {REVISION_TARGETS.map((t) => (
                <option key={t.key} value={t.key}>
                  {t.label}
                </option>
              ))}
            </select>
            <textarea
              className="lme-finalize-revise"
              value={revisionRequest}
              onChange={(e) => setRevisionRequest(e.target.value)}
              placeholder={
                revisionTarget === "ALL"
                  ? "What should change across the email?"
                  : `What should change in ${REVISION_TARGETS.find((t) => t.key === revisionTarget)?.label}? (only that section changes)`
              }
              rows={3}
              disabled={submitting || phase === "finalizing"}
            />
          </div>
        )}

        {phase === "error" && (
          <div className="write-confirm-error">⚠ {error}</div>
        )}
        {phase === "generated" && error && (
          <div className="write-confirm-error">
            ⚠ {error}. Same idempotency key reused on retry.
          </div>
        )}

        <div className="write-confirm-actions">
          <button
            className="write-confirm-cancel"
            onClick={onClose}
            disabled={phase === "finalizing" || savingScenario || extracting}
          >
            CLOSE
          </button>

          {phase === "preflight" && (
            <button
              className="write-confirm-confirm lme-finalize-submit"
              onClick={() => void onSaveAndContinue()}
              disabled={savingScenario || extracting}
              title="Write the 6 fields to Zoho and continue to the wizard"
            >
              {savingScenario ? "SAVING…" : "SAVE & CONTINUE →"}
            </button>
          )}

          {phase === "questions" && (
            <button
              className="write-confirm-confirm lme-finalize-submit"
              onClick={submitAnswers}
              disabled={submitting}
            >
              {submitting ? "ASKING…" : "SUBMIT ANSWERS"}
            </button>
          )}

          {(phase === "generated" || phase === "finalizing") && (
            <>
              <button
                className="lme-finalize-revise-btn"
                onClick={submitRevision}
                disabled={submitting || phase === "finalizing" || !revisionRequest.trim()}
                title="Iterate with Claude on the generated email"
              >
                {submitting ? "REVISING…" : "APPLY REVISION"}
              </button>
              <button
                className="write-confirm-confirm lme-finalize-finalize-btn"
                onClick={finalizeEmail}
                disabled={submitting || phase === "finalizing"}
                title="Lock this email, write to Zoho, create versioned Note"
              >
                {phase === "finalizing" ? "FINALIZING…" : "✓ FINALIZE EMAIL"}
              </button>
            </>
          )}

          {phase === "error" && (
            <button
              className="write-confirm-confirm"
              onClick={() => void callFinalize(chatHistory, null)}
              disabled={submitting}
            >
              RETRY
            </button>
          )}
        </div>

        {/* Phase B.2 FIX 4 — small footer link visible when the modal opened
            on a deal with a persisted finalized email (or any restored
            draft). Deliberately less prominent than the three action buttons.
            Hidden during finalize/save flights so user can't double-trigger. */}
        {draftSource && (phase === "generated" || phase === "questions") && (
          <button
            className="lme-finalize-start-over"
            onClick={() => void onStartFromScratch()}
            disabled={submitting || savingScenario || extracting}
            title="Discard the current state and start a fresh Q&A wizard"
          >
            ↻ Start from scratch
          </button>
        )}
      </div>
    </div>
  );
}

/** Phase B.2 FIX 5 — pre-flight scenario form. Renders the 6 required fields
 *  with picklists / number input. Pre-populated from LmeTab's scenario, but
 *  all 6 always visible (user can correct any of them). The EXTRACT button
 *  at the top runs Claude over the deal's Outlook emails + Zoho Notes and
 *  fills in any it found, marking each with a hint per the spec. */
function PreflightForm({
  preflight,
  setPreflight,
  missingFields,
  extracted,
  confidence,
  extracting,
  savingScenario,
  error,
  onExtract,
}: {
  preflight: PreflightFields;
  setPreflight: (next: PreflightFields) => void;
  missingFields: string[];
  extracted: Partial<Record<keyof PreflightFields, true>>;
  confidence: ExtractedConfidence;
  extracting: boolean;
  savingScenario: boolean;
  error: string | null;
  onExtract: () => void;
}) {
  const disabled = extracting || savingScenario;
  const update = (k: keyof PreflightFields, v: string) =>
    setPreflight({ ...preflight, [k]: v });

  const hint = (k: keyof PreflightFields) => {
    if (!extracted[k]) return null;
    const c = (confidence as Record<string, number | undefined>)[k];
    const pct = c != null ? ` (${Math.round(c * 100)}%)` : "";
    return (
      <span className="lme-preflight-hint">
        extracted from email — review{pct}
      </span>
    );
  };

  return (
    <div className="lme-preflight">
      <div className="lme-preflight-title">
        Some required fields are missing — fill in below or have Claude extract
        from this deal's emails.
      </div>
      {missingFields.length > 0 && (
        <div className="lme-preflight-missing">
          Missing: {missingFields.join(", ")}
        </div>
      )}
      <button
        className="lme-preflight-extract"
        onClick={onExtract}
        disabled={disabled}
        title="Ask Claude to read recent Outlook emails + Zoho Notes for this deal and fill in any scenario fields it can find"
      >
        {extracting ? "EXTRACTING…" : "✦ ASK CLAUDE TO EXTRACT FROM EMAILS"}
      </button>

      {error && <div className="write-confirm-error">⚠ {error}</div>}

      <div className="lme-preflight-grid">
        <label className="lme-preflight-field">
          <span className="lme-preflight-label">Loan amount</span>
          <input
            className="lme-preflight-input"
            type="text"
            inputMode="numeric"
            value={preflight.loanAmount}
            onChange={(e) => update("loanAmount", e.target.value)}
            placeholder="e.g. 850000"
            disabled={disabled}
          />
          {hint("loanAmount")}
        </label>

        <label className="lme-preflight-field">
          <span className="lme-preflight-label">Loan product</span>
          <select
            className="lme-preflight-input"
            value={preflight.loanProduct}
            onChange={(e) => update("loanProduct", e.target.value)}
            disabled={disabled}
          >
            <option value="">—</option>
            {LOAN_PRODUCTS.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
            {/* If extracted value isn't in our list, render it as an option so
                it's selectable rather than silently dropped. */}
            {preflight.loanProduct && !LOAN_PRODUCTS.includes(preflight.loanProduct as typeof LOAN_PRODUCTS[number]) && (
              <option value={preflight.loanProduct}>{preflight.loanProduct}</option>
            )}
          </select>
          {hint("loanProduct")}
        </label>

        <label className="lme-preflight-field">
          <span className="lme-preflight-label">Lien position</span>
          <select
            className="lme-preflight-input"
            value={preflight.lienPosition}
            onChange={(e) => update("lienPosition", e.target.value)}
            disabled={disabled}
          >
            <option value="">—</option>
            {LIEN_POSITIONS.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
            {preflight.lienPosition && !LIEN_POSITIONS.includes(preflight.lienPosition as typeof LIEN_POSITIONS[number]) && (
              <option value={preflight.lienPosition}>{preflight.lienPosition}</option>
            )}
          </select>
          {hint("lienPosition")}
        </label>

        <label className="lme-preflight-field">
          <span className="lme-preflight-label">Property type</span>
          <select
            className="lme-preflight-input"
            value={preflight.propType}
            onChange={(e) => update("propType", e.target.value)}
            disabled={disabled}
          >
            <option value="">—</option>
            {PROP_TYPES.map((p) => (
              <option key={p} value={p}>{p}</option>
            ))}
            {preflight.propType && !PROP_TYPES.includes(preflight.propType as typeof PROP_TYPES[number]) && (
              <option value={preflight.propType}>{preflight.propType}</option>
            )}
          </select>
          {hint("propType")}
        </label>

        <label className="lme-preflight-field">
          <span className="lme-preflight-label">State</span>
          <select
            className="lme-preflight-input"
            value={preflight.propState}
            onChange={(e) => update("propState", e.target.value.toUpperCase())}
            disabled={disabled}
          >
            <option value="">—</option>
            {US_STATES.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
            {preflight.propState && !US_STATES.includes(preflight.propState as typeof US_STATES[number]) && (
              <option value={preflight.propState}>{preflight.propState}</option>
            )}
          </select>
          {hint("propState")}
        </label>

        <label className="lme-preflight-field">
          <span className="lme-preflight-label">Occupancy</span>
          <select
            className="lme-preflight-input"
            value={preflight.occupancy}
            onChange={(e) => update("occupancy", e.target.value)}
            disabled={disabled}
          >
            <option value="">—</option>
            {OCCUPANCIES.map((o) => (
              <option key={o} value={o}>{o}</option>
            ))}
            {preflight.occupancy && !OCCUPANCIES.includes(preflight.occupancy as typeof OCCUPANCIES[number]) && (
              <option value={preflight.occupancy}>{preflight.occupancy}</option>
            )}
          </select>
          {hint("occupancy")}
        </label>
      </div>
    </div>
  );
}

/**
 * LME-6: click-to-edit text. Renders the value; click (or Enter/Space) swaps to
 * an input/textarea. Single-line commits on Enter or blur; multiline commits on
 * blur (Enter inserts a newline). Escape cancels. Commits only on real change.
 */
function EditableText({
  value,
  onCommit,
  multiline,
  placeholder,
}: {
  value: string;
  onCommit: (next: string) => void;
  multiline?: boolean;
  placeholder?: string;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  // Resync the draft to the underlying value when not editing (e.g. a revision
  // replaced the text out from under us).
  useEffect(() => {
    if (!editing) setDraft(value);
  }, [value, editing]);

  if (!editing) {
    return (
      <span
        className={"lme-editable" + (value ? "" : " lme-editable-empty")}
        role="button"
        tabIndex={0}
        title="Click to edit"
        onClick={() => setEditing(true)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setEditing(true);
          }
        }}
      >
        {value || placeholder || "—"}
      </span>
    );
  }

  const commit = () => {
    setEditing(false);
    if (draft !== value) onCommit(draft);
  };
  const cancel = () => {
    setDraft(value);
    setEditing(false);
  };

  return multiline ? (
    <textarea
      className="lme-editable-input lme-editable-textarea"
      value={draft}
      rows={3}
      autoFocus
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Escape") cancel();
      }}
    />
  ) : (
    <input
      className="lme-editable-input"
      value={draft}
      autoFocus
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === "Enter") commit();
        if (e.key === "Escape") cancel();
      }}
    />
  );
}

function DetailRows({
  label,
  rows,
  onEdit,
}: {
  label: string;
  rows: Array<{ key: string; value: string }>;
  /** LME-6 — when present, the key + value become click-to-edit. */
  onEdit?: (i: number, field: "key" | "value", value: string) => void;
}) {
  if (rows.length === 0) return null;
  return (
    <>
      <div className="lme-finalize-section-label">{label}</div>
      <div className="lme-finalize-rows">
        {rows.map((r, i) => (
          <div className="lme-finalize-row" key={i}>
            <span className="lme-finalize-row-key">
              {onEdit ? (
                <EditableText value={r.key} onCommit={(v) => onEdit(i, "key", v)} />
              ) : (
                r.key
              )}
            </span>
            <span className="lme-finalize-row-val">
              {onEdit ? (
                <EditableText value={r.value} onCommit={(v) => onEdit(i, "value", v)} />
              ) : (
                r.value
              )}
            </span>
          </div>
        ))}
      </div>
    </>
  );
}

/** Reshape the /finalize wire response (snake_case email keys) into the
 *  internal FinalizedEmail shape used for state + persist-finalize call. */
function wireToFinalized(r: FinalizeResponse): FinalizedEmail | null {
  if (r.phase !== "generated") return null;
  return {
    subject: r.email.subject,
    intro: r.email.intro,
    kpiBoxes: r.email.kpi_boxes,
    propertyRows: r.email.property_rows,
    borrowerRows: r.email.borrower_rows,
    useRows: r.email.use_rows,
    narrative: r.email.narrative,
    flags: r.email.flags,
    finalizedAt: r.email.finalized_at,
  };
}

/** Phase B.3 — specific toast for the scenario sync. Names the fields written
 *  so the user knows exactly what landed in Zoho (and when nothing did). */
function buildSyncToast(r: SyncScenarioResponse): string {
  const n = r.fields_written.length;
  if (n === 1) return `✓ Synced ${r.fields_written[0]} to Zoho`;
  if (n > 1) {
    return `✓ Synced ${n} fields to Zoho (${r.fields_written.join(", ")})`;
  }
  if (r.notes_appended) return "✓ Synced Q&A notes to Zoho";
  return "ℹ️ Answers captured in email. No new field values to sync to Zoho.";
}

/** Pulls numbered questions out of a Claude "questions" assistant turn for
 *  draft-restore (we already have the assistant text in chat_history). */
function extractQuestionsClient(text: string): string[] {
  const out: string[] = [];
  for (const line of text.split("\n")) {
    const m = line.match(/^\s*\d+\.\s+(.+)$/);
    if (m) out.push(m[1].trim());
  }
  return out;
}

function formatErr(err: unknown): string {
  if (err instanceof ApiError) {
    const body = err.body as { error?: { code?: string; message?: string } } | undefined;
    if (body?.error) {
      return `${body.error.code ?? "ERROR"}: ${body.error.message ?? ""} (HTTP ${err.status})`;
    }
    return `${err.code} (HTTP ${err.status})`;
  }
  if (err instanceof Error) return err.message;
  return "finalize_failed";
}
